import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GameStyles.css';

const GridGame = () => {
  // Game state
  const [gameStarted, setGameStarted] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [phase, setPhase] = useState('grid-display'); // grid-display, symmetry-question, grid-recall, results
  const [currentIndex, setCurrentIndex] = useState(0);

  // Grid state
  const [circlePositions, setCirclePositions] = useState([]); // All circle positions
  const [highlightedIndices, setHighlightedIndices] = useState([]); // Indices of highlighted circles in current level
  const [currentHighlightedIndex, setCurrentHighlightedIndex] = useState(null); // Current circle being shown
  const [userSelectedIndices, setUserSelectedIndices] = useState(new Set());

  // Symmetry state
  const [currentShapePair, setCurrentShapePair] = useState(null);
  const [symmetryAnswers, setSymmetryAnswers] = useState([]);
  const [correctSymmetryAnswers, setCorrectSymmetryAnswers] = useState([]);

  // Results
  const [levelResults, setLevelResults] = useState(null);

  // Timer (6 minutes = 360 seconds)
  const [time, setTime] = useState(360);
  const timerRef = useRef(null);
  const displayTimerRef = useRef(null);

  // Get grids per level
  const getGridsForLevel = (level) => {
    return level + 2; // Level 1: 3, Level 2: 4, Level 3: 5
  };

  // Timer effect
  useEffect(() => {
    if (gameStarted && phase !== 'results') {
      timerRef.current = setInterval(() => {
        setTime((prev) => {
          if (prev <= 1) {
            handleTimeOut();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameStarted, phase]);

  const moveToSymmetryQuestion = useCallback(() => {
    setPhase('symmetry-question');
    setCurrentShapePair(correctSymmetryAnswers[currentIndex]);
  }, [correctSymmetryAnswers, currentIndex]);

  // Grid display timer (2.5 seconds)
  useEffect(() => {
    if (phase === 'grid-display' && currentHighlightedIndex !== null) {
      displayTimerRef.current = setTimeout(() => {
        moveToSymmetryQuestion();
      }, 2500);
    }
    return () => {
      if (displayTimerRef.current) clearTimeout(displayTimerRef.current);
    };
  }, [phase, currentHighlightedIndex, moveToSymmetryQuestion]);

  const handleTimeOut = () => {
    alert('Time is up! Game over.');
    setGameStarted(false);
  };

  // Check if two circles overlap
  const circlesOverlap = (pos1, pos2, minDistance = 18) => {
    const dx = pos1.x - pos2.x;
    const dy = pos1.y - pos2.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < minDistance;
  };

  // Generate random circle positions (x, y coordinates)
  const generateRandomCirclePosition = () => {
    // Generate random x and y within a reasonable area (percentage-based)
    return {
      x: 15 + Math.random() * 70, // 15% to 85% of container width
      y: 15 + Math.random() * 70, // 15% to 85% of container height
      id: Math.random()
    };
  };

  // Generate all circle positions for display with no overlaps
  const generateCirclePositions = (numCircles) => {
    const positions = [];
    const maxAttempts = 100; // Maximum attempts to place a circle

    for (let i = 0; i < numCircles; i++) {
      let attempts = 0;
      let validPosition = false;
      let newPosition;

      while (!validPosition && attempts < maxAttempts) {
        newPosition = generateRandomCirclePosition();

        // Check if this position overlaps with any existing positions
        validPosition = true;
        for (let j = 0; j < positions.length; j++) {
          if (circlesOverlap(newPosition, positions[j])) {
            validPosition = false;
            break;
          }
        }

        attempts++;
      }

      if (validPosition) {
        positions.push(newPosition);
      }
    }

    return positions;
  };

  // Generate symmetry question
  const generateShapePair = () => {
    const shapes = ['triangle', 'square', 'circle', 'star', 'heart', 'arrow'];
    const isSymmetrical = Math.random() > 0.5;

    const shape1 = shapes[Math.floor(Math.random() * shapes.length)];

    return {
      shape1,
      shape2: shape1, // For simplicity, using same shape
      isSymmetrical,
      mirrored: isSymmetrical // If symmetrical, show mirrored version
    };
  };

  const startGame = () => {
    setGameStarted(true);
    setCurrentLevel(1);
    setTime(360);
    startLevel(1);
  };

  const startLevel = (level) => {
    const numGrids = getGridsForLevel(level);

    // Generate 6-10 circles randomly
    const totalCircles = 6 + Math.floor(Math.random() * 5); // Random number between 6 and 10
    const positions = generateCirclePositions(totalCircles);
    setCirclePositions(positions);

    // Generate random indices for highlighted circles
    const indices = [];
    const usedIndices = new Set();
    for (let i = 0; i < numGrids; i++) {
      let randomIndex;
      do {
        randomIndex = Math.floor(Math.random() * totalCircles);
      } while (usedIndices.has(randomIndex));
      usedIndices.add(randomIndex);
      indices.push(randomIndex);
    }

    // Generate symmetry questions
    const symmetryQuestions = [];
    for (let i = 0; i < numGrids; i++) {
      symmetryQuestions.push(generateShapePair());
    }

    setHighlightedIndices(indices);
    setCorrectSymmetryAnswers(symmetryQuestions);
    setSymmetryAnswers([]);
    setUserSelectedIndices(new Set());
    setCurrentIndex(0);
    setPhase('grid-display');

    // Show first highlighted circle
    setCurrentHighlightedIndex(indices[0]);
  };

  const handleSymmetryAnswer = (answer) => {
    const newAnswers = [...symmetryAnswers, answer];
    setSymmetryAnswers(newAnswers);

    const numGrids = getGridsForLevel(currentLevel);

    if (currentIndex + 1 < numGrids) {
      // Move to next grid
      setCurrentIndex(prev => prev + 1);
      setCurrentHighlightedIndex(highlightedIndices[currentIndex + 1]);
      setPhase('grid-display');
    } else {
      // All grids shown, move to recall phase
      setPhase('grid-recall');
    }
  };

  const toggleCircle = (circleIndex) => {
    const newSelected = new Set(userSelectedIndices);
    if (newSelected.has(circleIndex)) {
      newSelected.delete(circleIndex);
    } else {
      newSelected.add(circleIndex);
    }
    setUserSelectedIndices(newSelected);
  };

  const submitRecall = () => {
    // Calculate scores
    const highlightedSet = new Set(highlightedIndices);
    const userSet = new Set(userSelectedIndices);

    // Grid accuracy
    let correctCircles = 0;
    highlightedIndices.forEach(index => {
      if (userSet.has(index)) correctCircles++;
    });

    const gridAccuracy = (correctCircles / highlightedIndices.length) * 100;

    // Symmetry accuracy
    let correctSymmetry = 0;
    correctSymmetryAnswers.forEach((q, i) => {
      if (symmetryAnswers[i] === q.isSymmetrical) correctSymmetry++;
    });

    const symmetryAccuracy = (correctSymmetry / correctSymmetryAnswers.length) * 100;

    // Combined score
    const combinedScore = (gridAccuracy * 0.6) + (symmetryAccuracy * 0.4);
    const passed = combinedScore >= 70;

    setLevelResults({
      gridAccuracy: gridAccuracy.toFixed(1),
      symmetryAccuracy: symmetryAccuracy.toFixed(1),
      combinedScore: combinedScore.toFixed(1),
      passed,
      correctCircles,
      totalCircles: highlightedIndices.length,
      correctSymmetry,
      totalSymmetry: correctSymmetryAnswers.length,
      highlightedIndices: highlightedSet,
      userIndices: userSet
    });

    setPhase('results');
  };

  const handleContinue = () => {
    if (currentLevel < 3) {
      setCurrentLevel(prev => prev + 1);
      startLevel(currentLevel + 1);
    } else {
      // Game completed
      alert('Congratulations! You completed all levels!');
      setGameStarted(false);
    }
  };

  const handleRetry = () => {
    startLevel(currentLevel);
  };

  const renderShape = (shapeName, mirrored = false) => {
    const size = 120;
    const transform = mirrored ? 'scaleX(-1)' : '';

    switch (shapeName) {
      case 'triangle':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform }}>
            <polygon points="50,10 90,80 10,80" fill="#3b82f6" />
          </svg>
        );

      case 'square':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform }}>
            <rect x="20" y="20" width="60" height="60" fill="#ef4444" />
          </svg>
        );

      case 'circle':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform }}>
            <circle cx="50" cy="50" r="35" fill="#10b981" />
          </svg>
        );

      case 'star':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform }}>
            <polygon points="50,10 61,40 92,40 67,60 78,90 50,70 22,90 33,60 8,40 39,40" fill="#f59e0b" />
          </svg>
        );

      case 'heart':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform }}>
            <path d="M50,85 L20,55 Q10,45 10,35 Q10,20 25,20 Q40,20 50,35 Q60,20 75,20 Q90,20 90,35 Q90,45 80,55 Z" fill="#ec4899" />
          </svg>
        );

      case 'arrow':
        return (
          <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform }}>
            <polygon points="50,15 80,50 65,50 65,85 35,85 35,50 20,50" fill="#8b5cf6" />
          </svg>
        );

      default:
        return null;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="game-container grid-game">
      {!gameStarted ? (
        <div className="rules-container">
          <h1 className="game-title">Grid Challenge</h1>
          <div className="rules-content">
            <p>
              Test your memory and visual reasoning! You'll see highlighted grid positions while answering
              symmetry questions. The game has 3 levels with increasing difficulty.
            </p>
            <h3>How to Play</h3>
            <ul>
              <li>Watch carefully as a cell is highlighted in each grid (2.5 seconds)</li>
              <li>Answer whether two shapes are symmetrical or not</li>
              <li>After all rounds, click on all the cells that were highlighted</li>
              <li><strong>Level 1:</strong> 3 grids + 3 questions</li>
              <li><strong>Level 2:</strong> 4 grids + 4 questions</li>
              <li><strong>Level 3:</strong> 5 grids + 5 questions</li>
              <li>Pass threshold: 70% combined score (60% grid + 40% symmetry)</li>
              <li>Total time limit: 6 minutes</li>
            </ul>
            <button className="play-button" onClick={startGame}>
              Start Game
            </button>
          </div>
        </div>
      ) : (
        <div className="game-play-area">
          <div className="game-header">
            <div className="game-stat">Level {currentLevel}</div>
            <div className="game-stat">Time: {formatTime(time)}</div>
            <div className="game-name">Grid Challenge</div>
          </div>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(time / 360) * 100}%` }}></div>
          </div>

          <div className="game-content grid-challenge-content">
            {/* Grid Display Phase */}
            {phase === 'grid-display' && (
              <div className="grid-display-phase">
                <h2>Grid {currentIndex + 1} of {getGridsForLevel(currentLevel)}</h2>
                <p>Remember this position!</p>
                <div className="circle-container">
                  {circlePositions.map((pos, index) => (
                    <div
                      key={index}
                      className={`circle ${currentHighlightedIndex === index ? 'circle-highlighted' : ''}`}
                      style={{
                        left: `${pos.x}%`,
                        top: `${pos.y}%`
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Symmetry Question Phase */}
            {phase === 'symmetry-question' && currentShapePair && (
              <div className="symmetry-phase">
                <h2>Question {currentIndex + 1} of {getGridsForLevel(currentLevel)}</h2>
                <p>Are these figures symmetrical?</p>
                <div className="shape-pair">
                  <div className="shape-container">
                    {renderShape(currentShapePair.shape1, false)}
                  </div>
                  <div className="shape-container">
                    {renderShape(currentShapePair.shape2, currentShapePair.mirrored)}
                  </div>
                </div>
                <div className="symmetry-buttons">
                  <button
                    className="symmetry-btn symmetrical-btn"
                    onClick={() => handleSymmetryAnswer(true)}
                  >
                    Symmetrical
                  </button>
                  <button
                    className="symmetry-btn not-symmetrical-btn"
                    onClick={() => handleSymmetryAnswer(false)}
                  >
                    Not Symmetrical
                  </button>
                </div>
              </div>
            )}

            {/* Grid Recall Phase */}
            {phase === 'grid-recall' && (
              <div className="grid-recall-phase">
                <h2>Click all circles that were highlighted</h2>
                <p>Selected: {userSelectedIndices.size} circles</p>
                <div className="circle-container">
                  {circlePositions.map((pos, index) => (
                    <div
                      key={index}
                      className={`circle recall-circle ${userSelectedIndices.has(index) ? 'circle-selected' : ''}`}
                      style={{
                        left: `${pos.x}%`,
                        top: `${pos.y}%`
                      }}
                      onClick={() => toggleCircle(index)}
                    />
                  ))}
                </div>
                <button className="submit-btn" onClick={submitRecall}>
                  Submit Answer
                </button>
              </div>
            )}

            {/* Results Phase */}
            {phase === 'results' && levelResults && (
              <div className="results-phase">
                <h2>Level {currentLevel} Results</h2>

                <div className="results-grid-display">
                  <h3>Your Answer:</h3>
                  <div className="circle-container">
                    {circlePositions.map((pos, index) => {
                      const isHighlighted = levelResults.highlightedIndices.has(index);
                      const isUserSelected = levelResults.userIndices.has(index);
                      const isCorrect = isHighlighted && isUserSelected;
                      const isMissed = isHighlighted && !isUserSelected;
                      const isWrong = !isHighlighted && isUserSelected;

                      return (
                        <div
                          key={index}
                          className={`circle result-circle ${
                            isCorrect ? 'circle-correct' : ''
                          } ${isMissed ? 'circle-missed' : ''} ${isWrong ? 'circle-wrong' : ''}`}
                          style={{
                            left: `${pos.x}%`,
                            top: `${pos.y}%`
                          }}
                        />
                      );
                    })}
                  </div>
                  <div className="result-legend">
                    <span><span className="legend-box correct"></span> Correct</span>
                    <span><span className="legend-box missed"></span> Missed</span>
                    <span><span className="legend-box wrong"></span> Wrong</span>
                  </div>
                </div>

                <div className="score-display">
                  <div className="score-item">
                    <h3>Grid Accuracy</h3>
                    <p className="score-value">{levelResults.gridAccuracy}%</p>
                    <p className="score-detail">
                      {levelResults.correctCircles} / {levelResults.totalCircles} circles
                    </p>
                  </div>

                  <div className="score-item">
                    <h3>Symmetry Accuracy</h3>
                    <p className="score-value">{levelResults.symmetryAccuracy}%</p>
                    <p className="score-detail">
                      {levelResults.correctSymmetry} / {levelResults.totalSymmetry} questions
                    </p>
                  </div>

                  <div className="score-item combined">
                    <h3>Combined Score</h3>
                    <p className="score-value combined-score">{levelResults.combinedScore}%</p>
                    <p className="score-detail">Grid (60%) + Symmetry (40%)</p>
                  </div>
                </div>

                <div className="result-status">
                  {levelResults.passed ? (
                    <>
                      <div className="pass-message">
                        <h2>✓ Passed!</h2>
                        <p>Great job! You scored above 70%</p>
                      </div>
                      {currentLevel < 3 ? (
                        <button className="continue-btn" onClick={handleContinue}>
                          Continue to Level {currentLevel + 1}
                        </button>
                      ) : (
                        <button className="continue-btn" onClick={() => setGameStarted(false)}>
                          Game Complete! Play Again
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="fail-message">
                        <h2>✗ Not Passed</h2>
                        <p>You need 70% or higher to continue</p>
                      </div>
                      <button className="retry-btn" onClick={handleRetry}>
                        Retry Level {currentLevel}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GridGame;
